import type {
  AccountHistoricalTrendResponse,
  MonthlyNetWorthChartInterface,
} from '@/models/AccountViewModel';

type TrendPoint =
  AccountHistoricalTrendResponse['Records'][number]['data'][number];

export interface NetWorthTrendSeries {
  combined: AccountHistoricalTrendResponse | null;
  deposits: AccountHistoricalTrendResponse | null;
  liabilities: AccountHistoricalTrendResponse | null;
}

const toApiDay = (date: string) => date.slice(0, 10);

function sortedPoints(response: AccountHistoricalTrendResponse | null) {
  const points: TrendPoint[] = response?.Records[0]?.data ?? [];
  return points
    .map((point) => ({ day: toApiDay(point.Date), value: point.Value }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

export function valuesAtOrBefore(
  response: AccountHistoricalTrendResponse | null,
  days: string[],
): number[] {
  const points = sortedPoints(response);
  let cursor = 0;
  let value = 0;

  return days.map((day) => {
    while (cursor < points.length && points[cursor].day <= day) {
      value = points[cursor].value;
      cursor += 1;
    }
    return value;
  });
}

export function buildMonthlyNetWorth(
  monthEndDates: string[],
  series: NetWorthTrendSeries,
): MonthlyNetWorthChartInterface[] {
  const netWorth = valuesAtOrBefore(series.combined, monthEndDates);
  const assets = valuesAtOrBefore(series.deposits, monthEndDates);
  const liabilities = valuesAtOrBefore(series.liabilities, monthEndDates);

  return monthEndDates.map((monthEnd, index) => ({
    date: new Date(monthEnd),
    assets: assets[index],
    liabilities: Math.abs(liabilities[index]),
    netWorth: netWorth[index],
  }));
}
